import { useState } from 'react';
import { ScrollText, WifiOff } from 'lucide-react';
import { useGetAuditLogQuery, getApiError, type AuditEvent } from '@/store/api';
import { useAuthStore } from '@/store/auth.store';
import { eventMark } from '@/components/ui/Badge';
import { PageLayout } from '@/components/ui/PageLayout';
import { DataTable, type Column } from '@/components/ui/DataTable';
import { Pagination } from '@/components/ui/Pagination';
import { Button } from '@/components/ui/Button';
import { Input, Select } from '@/components/ui/Field';
import { styles } from '@/styles';

const EVENT_TYPES = [
  { value: '', label: 'All events' },
  { value: 'released', label: 'Released' },
  { value: 'failed_attempt', label: 'Failed attempt' },
  { value: 'suspicious_flagged', label: 'Flagged' },
  { value: 'delegate_added', label: 'Delegate added' },
  { value: 'delegate_cancelled', label: 'Delegate cancelled' },
  { value: 'code_revoked', label: 'Code revoked' },
];

const PAGE_SIZE = 25;

const stampFmt = new Intl.DateTimeFormat('en-GH', {
  day: '2-digit',
  month: 'short',
  hour: '2-digit',
  minute: '2-digit',
});

/*
  The audit log.

  This table is the record of who released which child, so it is the one screen
  in the product where nothing may be rounded off: identifiers stay monospaced
  and un-truncated in the DOM even where the column is narrow, and an offline
  event is marked rather than left to look like an ordinary one — an offline
  release was verified against a cached bundle, not against the server, which is
  exactly the distinction someone reviewing an incident is looking for.
*/
export function AuditPage() {
  const institutionId = useAuthStore((s) => s.user?.institutionId ?? '');

  const [page, setPage] = useState(1);
  const [studentId, setStudentId] = useState('');
  const [eventType, setEventType] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  const { data, isLoading, isError, error } = useGetAuditLogQuery(
    {
      institutionId,
      page,
      pageSize: PAGE_SIZE,
      studentId: studentId || undefined,
      eventType: eventType || undefined,
      from: from || undefined,
      to: to || undefined,
    },
    { skip: !institutionId },
  );

  const hasFilters = Boolean(studentId || eventType || from || to);

  const reset = () => {
    setPage(1);
    setStudentId('');
    setEventType('');
    setFrom('');
    setTo('');
  };

  /* Any filter change goes back to page 1 — staying on page 6 of a result set
     that now has two pages shows an empty table and reads as "no events". */
  const onFilter = <T,>(setter: (v: T) => void) => (v: T) => {
    setter(v);
    setPage(1);
  };

  const columns: Column<AuditEvent>[] = [
    {
      key: 'eventType',
      header: 'Event',
      render: (e) => eventMark(e.eventType),
      className: 'w-[150px]',
    },
    {
      key: 'studentId',
      header: 'Student',
      render: (e) => <span className="font-mono text-[11px] text-muted-foreground">{e.studentId}</span>,
    },
    {
      key: 'gateOfficerId',
      header: 'Gate officer',
      render: (e) => (
        <span className="font-mono text-[11px] text-muted-foreground">
          {e.gateOfficerId ? `…${e.gateOfficerId.slice(-8)}` : '—'}
        </span>
      ),
      className: 'max-md:hidden',
    },
    {
      key: 'deviceId',
      header: 'Device',
      render: (e) => (
        <span className="font-mono text-[11px] text-muted-foreground">
          {e.deviceId ? `…${e.deviceId.slice(-8)}` : '—'}
        </span>
      ),
      className: 'max-lg:hidden',
    },
    {
      key: 'isOfflineEvent',
      header: 'Mode',
      render: (e) =>
        e.isOfflineEvent ? (
          <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-[hsl(var(--record-ink-warning))]">
            <WifiOff size={11} />
            Offline
          </span>
        ) : (
          <span className="text-[11px] text-muted-foreground">Online</span>
        ),
      className: 'w-[90px]',
    },
    {
      key: 'createdAt',
      header: 'When',
      render: (e) => (
        <time dateTime={e.createdAt} className="text-[11px] tabular-nums text-muted-foreground">
          {stampFmt.format(new Date(e.createdAt))}
        </time>
      ),
      className: 'w-[130px]',
    },
  ];

  return (
    <PageLayout
      title="Audit log"
      subtitle="Every gate event, append-only"
      breadcrumbs={[{ label: 'Gate' }, { label: 'Audit log' }]}
      actions={
        hasFilters ? (
          <Button variant="ghost" size="sm" onClick={reset}>
            Clear filters
          </Button>
        ) : undefined
      }
      toolbar={
        <>
          <Input
            placeholder="Student ID"
            aria-label="Filter by student ID"
            className="w-44"
            value={studentId}
            onChange={(e) => onFilter(setStudentId)(e.target.value)}
          />
          <Select
            aria-label="Filter by event type"
            className="w-44"
            value={eventType}
            onChange={(e) => onFilter(setEventType)(e.target.value)}
          >
            {EVENT_TYPES.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </Select>
          <div className="flex items-center gap-1.5">
            <Input
              type="date"
              aria-label="From date"
              className="w-[140px]"
              value={from}
              max={to || undefined}
              onChange={(e) => onFilter(setFrom)(e.target.value)}
            />
            <span className={styles.label}>to</span>
            <Input
              type="date"
              aria-label="To date"
              className="w-[140px]"
              value={to}
              min={from || undefined}
              onChange={(e) => onFilter(setTo)(e.target.value)}
            />
          </div>
        </>
      }
    >
      <DataTable
        columns={columns}
        rows={data?.items ?? []}
        rowKey={(e) => e._id}
        loading={isLoading}
        error={isError || undefined}
        errorMessage={isError ? getApiError(error) : undefined}
        emptyTitle={hasFilters ? 'No events match these filters' : 'No events yet'}
        emptyDescription={
          hasFilters
            ? 'Widen the date range or clear the filters to see more.'
            : 'Events appear here as gate officers verify codes.'
        }
        emptyAction={
          hasFilters ? (
            <Button variant="secondary" size="sm" icon={<ScrollText size={13} />} onClick={reset}>
              Clear filters
            </Button>
          ) : undefined
        }
        footer={
          (data?.totalPages ?? 1) > 1 ? (
            <Pagination
              page={page}
              totalPages={data!.totalPages}
              total={data!.total}
              pageSize={PAGE_SIZE}
              onPageChange={setPage}
            />
          ) : undefined
        }
      />
    </PageLayout>
  );
}
